/**
 * P3 — Camera-PiP layout geometry for proctoring overlay mapping.
 *
 * Proctoring detects on the raw webcam feed, but the saved recording is the
 * screen-share. To draw the detection overlay in the right place at report
 * time, we tell the backend WHERE the on-screen camera tile sits inside the
 * recording (which, for an "entire screen" share, is the physical monitor).
 *
 * This module holds the PURE math so it can be unit-tested without a DOM:
 * given the camera tile's viewport rect and the window's screen metrics, it
 * returns the tile's box as fractions (0..1) of the screen frame.
 *
 * The screen-coordinate mapping is a standard approximation (window origin +
 * top chrome + side border). Fractions are device-pixel-ratio invariant
 * because numerator and denominator are both CSS pixels and the recording
 * scales uniformly. Exact offsets can need live calibration across OS / zoom.
 */
const clamp01 = (v) => Math.max(0, Math.min(1, v));
/**
 * Tile box as a fraction of the VIEWPORT. Both `rect` and the viewport are in
 * the SAME (current-zoom) CSS pixels, so the ratio is invariant under browser
 * zoom, device-pixel-ratio, OS scaling and screen size. Exact whenever the
 * viewport fills the shared surface (fullscreen, or tab/window share).
 */
export function viewportFraction(rect, innerW, innerH) {
    if (!innerW || !innerH || rect.width <= 0 || rect.height <= 0)
        return null;
    return {
        x: clamp01(rect.left / innerW),
        y: clamp01(rect.top / innerH),
        w: clamp01(rect.width / innerW),
        h: clamp01(rect.height / innerH),
    };
}
/**
 * Map a tile's viewport rect to a fraction of the SCREEN recording, WITHOUT
 * needing fullscreen. Built entirely from viewport-relative CSS pixels, so it
 * is zoom-, DPR- and screen-size-invariant:
 *
 *   • Horizontally a maximized window fills the screen, so x/w are viewport
 *     fractions (plus any tiny side border).
 *   • Vertically the browser chrome (address/tab bar) sits above the viewport;
 *     its height ≈ outerHeight − innerHeight, so the tile is offset by it and
 *     the total screen height ≈ innerHeight + chrome.
 *
 * EXACT when the window is maximized (and, as a special case, when fullscreen —
 * chrome ≈ 0 → pure viewport fraction). Approximate only for a floating /
 * non-maximized window or a secondary monitor, where the browser cannot know
 * the window's on-screen position (taskbar/offset unaccounted).
 */
export function viewportToScreenFraction(rect, m) {
    if (!m.innerW || !m.innerH || rect.width <= 0 || rect.height <= 0)
        return null;
    const chromeTop = Math.max(0, (m.outerH || m.innerH) - m.innerH);
    const chromeSide = Math.max(0, ((m.outerW || m.innerW) - m.innerW) / 2);
    const totalH = m.innerH + chromeTop;
    const totalW = m.innerW + chromeSide * 2;
    return {
        x: clamp01((chromeSide + rect.left) / totalW),
        y: clamp01((chromeTop + rect.top) / totalH),
        w: clamp01(rect.width / totalW),
        h: clamp01(rect.height / totalH),
    };
}
/**
 * OV-2/OV-4: is the camera tile actually visible on the shared screen right now?
 * False when the tab is hidden (candidate switched to another tab/app — the PiP
 * isn't painted on the recording), or the camera is off / has no stream. Drives
 * the `camVisible` flag on the emitted layout so the report overlay hides for
 * exactly those spans.
 */
export function cameraTileVisible(documentHidden, camEnabled, hasStream) {
    return !documentHidden && !!camEnabled && !!hasStream;
}
/**
 * OV-3: map a tile's viewport rect to a fraction of the WHOLE SCREEN using the
 * window's on-screen geometry. The screen share captures the entire monitor, so
 * when the browser is NOT fullscreen/maximized the recording is the monitor and
 * the tile sits at the window's screen offset — which `viewportToScreenFraction`
 * (viewport-only) cannot know, so its overlay drifts once fullscreen is exited.
 *
 * Geometry (all CSS pixels, so the fraction stays DPR/zoom-invariant — the
 * recording scales uniformly):
 *   • viewport screen origin = (screenX, screenY)
 *   • tile screen pos = origin + rect;  fraction = pos / screen size
 *
 * CRITICAL: `screenX`/`screenY` are, per CSSOM View, the position of the
 * VIEWPORT (client area) relative to the screen — they ALREADY exclude the
 * browser chrome (title/tab/address/bookmarks bars). Do NOT add
 * `outerH − innerH` back on top: that double-counts the chrome and shifts the
 * tile (and every landmark) DOWN by the chrome height whenever the window is
 * not fullscreen. The bug was invisible in fullscreen only because chrome ≈ 0
 * and screenX/Y ≈ 0 there.
 *
 * Subsumes the fullscreen/maximized case (screenX≈0, screenY≈0 → pure viewport
 * fraction). Best-effort on a secondary monitor (screenX/Y are virtual-desktop
 * coords that can exceed the primary screen); fractions are clamped to [0,1].
 * Returns null when screen metrics are unusable so the caller can fall back to
 * the viewport-only mapping.
 */
export function screenRectFraction(rect, m) {
    if (!m.screenW || !m.screenH || rect.width <= 0 || rect.height <= 0)
        return null;
    // screenX/screenY are the viewport origin on the screen (chrome already
    // excluded) — see the note above.
    const originX = m.screenX;
    const originY = m.screenY;
    return {
        x: clamp01((originX + rect.left) / m.screenW),
        y: clamp01((originY + rect.top) / m.screenH),
        w: clamp01(rect.width / m.screenW),
        h: clamp01(rect.height / m.screenH),
    };
}
/**
 * OV: object-fit placement of a source frame inside a tile.
 *
 * Detection landmarks are normalized (0..1) to the RAW webcam frame, but the
 * on-screen tile shows that frame under `object-fit` — so with a mismatched
 * aspect ratio the frame is cropped (cover) or letterboxed (contain) and the
 * displayed content occupies only a sub-region of the tile. This returns the
 * source frame's displayed rect in TILE-normalized units (centered), so a
 * landmark maps as:  tx = ox + nx*sw,  ty = oy + ny*sh.
 *
 * With `cover` the overflowing axis has size > 1 and a negative offset (the
 * cropped part sits outside [0,1]); with `contain` the short axis has size < 1
 * and a positive offset (letterbox bars). Identity {0,0,1,1} when either aspect
 * is unusable — legacy behaviour (stretch to fill).
 */
export function objectFitSourceRect(fit, srcAspect, // srcW / srcH
tileAspect, // tileW / tileH (displayed px)
// VA-28255 item 8: the crop anchor. `object-position` decides WHICH part of
// the overflow is cut off (cover) or where the letterbox sits (contain);
// hardcoding the centre is right only for the default. Defaults to centre, so
// every existing caller and every stored row keeps its current behaviour.
objectPos = [0.5, 0.5]) {
    if (!(srcAspect > 0) || !(tileAspect > 0))
        return { ox: 0, oy: 0, sw: 1, sh: 1 };
    const ratio = tileAspect / srcAspect;
    let sw = 1;
    let sh = 1;
    if (fit === 'contain') {
        if (ratio >= 1)
            sw = 1 / ratio; // tile wider than source → pillarbox (bars L/R)
        else
            sh = ratio; //                tile taller than source → letterbox (bars T/B)
    }
    else {
        // cover
        if (ratio >= 1)
            sh = ratio; //     tile wider than source → crop top/bottom
        else
            sw = 1 / ratio; //            tile taller than source → crop left/right
    }
    // `(1 - sw) * px` generalises `(1 - sw) / 2`. For cover, `1 - sw` is negative
    // (the source overflows) and px picks which edge is kept: 0 keeps the left,
    // 1 the right. For contain it is positive and px places the bar.
    const px = Number.isFinite(objectPos === null || objectPos === void 0 ? void 0 : objectPos[0]) ? Math.min(1, Math.max(0, objectPos[0])) : 0.5;
    const py = Number.isFinite(objectPos === null || objectPos === void 0 ? void 0 : objectPos[1]) ? Math.min(1, Math.max(0, objectPos[1])) : 0.5;
    return { ox: (1 - sw) * px, oy: (1 - sh) * py, sw, sh };
}
/** Last layout transition at or before playback time `t` (earliest if before all). */
export function findLayoutAt(rows, t) {
    if (!rows || !rows.length)
        return null;
    // VA-28145: before the first row we have NO observation, so draw nothing.
    // Returning `rows[0]` projected the first geometry we ever saw backwards over
    // the opening of the interview — presenting a guess as a measurement, at
    // exactly the point where a candidate is still arranging their windows and it
    // is least likely to hold.
    if (t < rows[0].t)
        return null;
    let lo = 0;
    let hi = rows.length - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (rows[mid].t <= t)
            lo = mid + 1;
        else
            hi = mid - 1;
    }
    return rows[hi] || rows[0];
}
/**
 * P4: compose a WEBCAM-normalized landmark (0..1 of the camera frame) into a
 * SCREEN-normalized point (0..1 of the recording frame), applying the layout
 * row's mirror flip and camera-box placement.
 *
 * Returns null when the camera is not visible on the shared surface (overlay
 * should draw nothing). With no box (legacy data) it falls back to identity so
 * old reports — whose landmarks were already in screen space — still render.
 */
export function composeToScreenFraction(nx, ny, row) {
    if (row && row.camVisible === false)
        return null;
    const box = row === null || row === void 0 ? void 0 : row.camBox;
    const mirrored = !!(row === null || row === void 0 ? void 0 : row.mirrored);
    const hasBox = !!(box && box.w > 0 && box.h > 0);
    const mx = mirrored ? 1 - nx : nx;
    return {
        x: hasBox ? box.x + mx * box.w : mx,
        y: hasBox ? box.y + ny * box.h : ny,
    };
}
/**
 * VA-28145: how far past a row's confirmed validity a webcam landmark is still
 * mapped. The frontend re-confirms every ~5s, so this allows three missed
 * heartbeats before the track is treated as ended. Deliberately not tight —
 * dropping landmarks over a single late heartbeat would trade one wrong answer
 * for another.
 */
export const STALE_LAYOUT_TOLERANCE_SEC = 15;
/**
 * The full webcam → recording-frame mapping at playback time `t`, extracted
 * verbatim from the v1 report overlay (ProctoringVideoOverlay) so the v2 player
 * maps `space: "webcam"` rows through exactly the same maths. v1 calls this and
 * its outputs are unchanged — pinned by proctor-layout-webcam-map.test.ts.
 *
 * `content` is the video's displayed content rect in px; only its aspect
 * matters, and it is taken as w/h exactly as v1 computed it.
 *
 * Detection runs on the webcam feed, so landmarks are normalized to the camera
 * frame. The layout track says where the camera tile sits in the recording (as
 * a fraction of the screen) at this time, is it visible, and whether it's shown
 * mirrored. Compose: landmark → object-fit crop → [mirror] → camBox. With no
 * layout data the mapping is the identity, so legacy reports (landmarks already
 * in screen space) still render.
 */
export function webcamToFrameMapper(layout, t, content) {
    const layoutRow = findLayoutAt(layout, t);
    if (layoutRow && layoutRow.camVisible === false)
        return { kind: 'hidden' }; // camera not in the recording
    // VA-28145: refuse to draw on a box whose ORIGIN we do not actually know.
    //
    // The tile's size was never in doubt; its origin was, by up to 585px on
    // interview-b81757ff — 30% of frame width, and on the coding PiP 1.7x the
    // tile's own width, so landmarks rendered with zero overlap with the real
    // tile. They appeared on the candidate's editor and on the address bar, and
    // looked exactly as authoritative as a correct overlay.
    //
    // Drawing nothing is the better failure. A reviewer who sees no landmarks
    // asks why; one who sees landmarks on the wrong window believes them.
    //
    // `mapVersion` gates this: rows recorded before VA-28145 carry no trust
    // information and take the legacy path unchanged, because absence of the
    // flag is not evidence of a bad origin.
    // VA-28154: an INFERRED origin is drawn, not withheld.
    //
    // A floating window is now placed from `screenX/Y`, whose meaning the probe
    // establishes whenever the window passes through a maximized state. Until
    // that happens the placement rests on the spec's reading rather than an
    // observed one, and could be off by the chrome height. Drawing it with that
    // said is more useful than a blank frame: the reviewer sees the landmarks
    // and knows what not to conclude from a small offset.
    const originAssumed = !!(layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.originAssumed);
    const originUntrusted = !!((layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.mapVersion) && layoutRow.originTrusted === false) && !originAssumed;
    const notice = originUntrusted ? 'origin' : originAssumed ? 'inferred' : null;
    if (originUntrusted)
        return { kind: 'withheld', notice };
    // And refuse to project a stale observation forward. The frontend
    // re-confirms every ~5s and the backend bumps `tEnd` in place, so a `tEnd`
    // well behind the playhead means we STOPPED observing rather than that
    // nothing changed — the two are indistinguishable without it, which is what
    // `tEnd` was added to fix. The tolerance is generous: this is meant to catch
    // a track that ended, not to police jitter around the heartbeat.
    if ((layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.tEnd) != null && t - layoutRow.tEnd > STALE_LAYOUT_TOLERANCE_SEC) {
        return { kind: 'withheld', notice };
    }
    // ── object-fit crop ──
    // Landmarks are normalized to the RAW webcam frame, but the tile shows that
    // frame under object-fit (cover on both the live meeting tile and the PiP).
    // When the camera aspect ≠ the tile aspect the frame is cropped, so a linear
    // stretch across the whole tile misplaces every point (the visible symptom:
    // dots drift off the eyes, worst in fullscreen where a 4:3 cam covers a 16:9
    // screen). Reproduce the crop: source (nx,ny) → tile-normalized (tx,ty)
    // BEFORE the mirror + camera-box placement. Skipped for legacy rows with no
    // srcW/srcH, or when the tile fills the frame (camBox absent).
    const box = layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.camBox;
    let crop = { ox: 0, oy: 0, sw: 1, sh: 1 };
    if ((layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.srcW) && (layoutRow === null || layoutRow === void 0 ? void 0 : layoutRow.srcH) && box && box.w > 0 && box.h > 0) {
        const srcAspect = layoutRow.srcW / layoutRow.srcH;
        // Tile aspect in displayed px: camBox is a fraction of the recording, whose
        // content rect is content.w × content.h px, so the tile is (box.w*content.w) wide.
        const tileAspect = (box.w * content.w) / (box.h * content.h);
        // VA-28255 item 8: use the tile's recorded object-position rather than
        // assuming centre. Rows written before it was captured fall back to
        // centre, which is what they were actually rendered with.
        const m = layoutRow.metrics;
        const anchor = m && typeof m.objectPosX === 'number' && typeof m.objectPosY === 'number'
            ? [m.objectPosX, m.objectPosY]
            : [0.5, 0.5];
        crop = objectFitSourceRect(layoutRow.fit === 'contain' ? 'contain' : 'cover', srcAspect, tileAspect, anchor);
    }
    // composeToScreenFraction applies the mirror flip + camera-box placement.
    // x depends only on nx and y only on ny (crop is separable), so keep the
    // single-arg fast path.
    return {
        kind: 'ok',
        notice,
        row: layoutRow,
        x: (nx) => {
            const tx = crop.ox + nx * crop.sw;
            const s = composeToScreenFraction(tx, 0, layoutRow);
            return s ? s.x : tx;
        },
        y: (ny) => {
            const ty = crop.oy + ny * crop.sh;
            const s = composeToScreenFraction(0, ty, layoutRow);
            return s ? s.y : ty;
        },
    };
}
/** True when two boxes are equal within a small epsilon (avoids resize jitter). */
export function camBoxEquals(a, b, eps = 0.002) {
    if (!a || !b)
        return a === b;
    return (Math.abs(a.x - b.x) < eps &&
        Math.abs(a.y - b.y) < eps &&
        Math.abs(a.w - b.w) < eps &&
        Math.abs(a.h - b.h) < eps);
}
/** Read the mirror state of a tile element from its computed transform. */
export function elementIsMirrored(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle)
        return false;
    const t = window.getComputedStyle(el).transform || '';
    // matrix(a, b, c, d, e, f): a < 0 => horizontally flipped (scaleX(-1)).
    const m = t.match(/matrix\(([^)]+)\)/);
    if (m) {
        const a = parseFloat(m[1].split(',')[0]);
        if (!Number.isNaN(a) && a < 0)
            return true;
    }
    return false;
}
/** Read object-fit ('cover' | 'contain') of a tile element. */
export function elementFit(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle)
        return 'cover';
    return window.getComputedStyle(el).objectFit === 'contain' ? 'contain' : 'cover';
}
/**
 * VA-28255 Phase 11: the tile's `object-position`, as a 0..1 pair.
 *
 * The crop maths has always assumed CENTRE — `(1 - sw) / 2` — and nothing reads
 * the actual value. Both tiles happen to use the default today, so the
 * assumption is currently true and silently so: the day someone writes
 * `object-position: top`, every landmark moves and nothing explains why.
 *
 * `getComputedStyle` resolves keywords to percentages ("50% 50%", "100% 0%"),
 * so the percentage case is the only one that needs handling. A pixel value
 * cannot be resolved to a fraction without the element size and is reported as
 * centre — recorded here rather than silently, because that IS a gap.
 */
export function elementObjectPosition(el) {
    var _a;
    if (!el || typeof window === 'undefined' || !window.getComputedStyle)
        return [0.5, 0.5];
    const raw = String(window.getComputedStyle(el).objectPosition || '').trim();
    if (!raw)
        return [0.5, 0.5];
    const parts = raw.split(/\s+/);
    const toFraction = (v) => {
        if (!v)
            return 0.5;
        const m = v.match(/^(-?[\d.]+)%$/);
        if (!m)
            return 0.5;
        const n = Number(m[1]);
        return Number.isFinite(n) ? Math.min(1, Math.max(0, n / 100)) : 0.5;
    };
    return [toFraction(parts[0]), toFraction((_a = parts[1]) !== null && _a !== void 0 ? _a : parts[0])];
}
/**
 * Build a full proctor_layout payload from a live camera tile element.
 * Returns null when the tile isn't rendered/visible (caller emits
 * camVisible:false separately in that case).
 */
export function buildCameraLayout(el, ts, 
// VA-28145: what the candidate actually shared. Optional so every existing
// caller keeps compiling and keeps today's behaviour — an absent surface is
// `unknown`, which maps exactly as before and is marked untrusted.
// VA-28255 Phase 2: the FULL surface, not just its kind. The dimensions were
// being read by `readCaptureSurface` and then discarded at this boundary —
// the one value that says what coordinate space the recording is in.
surface, which) {
    var _a, _b, _c;
    if (!el || typeof window === 'undefined')
        return null;
    // offsetParent === null => not rendered (display:none / detached).
    if (el.offsetParent === null && el.getClientRects().length === 0)
        return null;
    const r = el.getBoundingClientRect();
    const rect = { left: r.left, top: r.top, width: r.width, height: r.height };
    const innerW = window.innerWidth || 0;
    const innerH = window.innerHeight || 0;
    const outerW = window.outerWidth || innerW;
    const outerH = window.outerHeight || innerH;
    // OV-3: prefer the SCREEN-relative mapping (uses the window's on-screen origin),
    // which is correct whether the browser is fullscreen, maximized, OR a floating
    // window somewhere on the monitor — the screen share captures the whole monitor.
    // Fall back to the viewport-only mapping when screen metrics are unavailable
    // (e.g. SSR / a non-"entire screen" share where the viewport fills the surface).
    const screen = typeof window !== 'undefined' ? window.screen : undefined;
    const metrics = {
        innerW,
        innerH,
        outerW,
        outerH,
        screenX: typeof window.screenX === 'number' ? window.screenX : 0,
        screenY: typeof window.screenY === 'number' ? window.screenY : 0,
        availW: screen && typeof screen.availWidth === 'number' ? screen.availWidth : undefined,
        availH: screen && typeof screen.availHeight === 'number' ? screen.availHeight : undefined,
        // Non-standard but present in Chrome and Firefox; absent is treated as 0,
        // which is the right answer for a desktop whose furniture is not on the
        // top/left edge.
        availLeft: screen && typeof screen.availLeft === 'number'
            ? screen.availLeft
            : 0,
        availTop: screen && typeof screen.availTop === 'number'
            ? screen.availTop
            : 0,
        screenW: (screen === null || screen === void 0 ? void 0 : screen.width) || 0,
        screenH: (screen === null || screen === void 0 ? void 0 : screen.height) || 0,
        // VA-28255 item 7: the capture's own dimensions and the css-px-to-device-px
        // scale, so the mapping can be done without mixing zoomed and unzoomed px.
        captureW: Math.round((surface === null || surface === void 0 ? void 0 : surface.trackW) || 0),
        captureH: Math.round((surface === null || surface === void 0 ? void 0 : surface.trackH) || 0),
        dpr: typeof window.devicePixelRatio === 'number' ? window.devicePixelRatio : 1,
    };
    // VA-28145: map into the shared surface's own coordinate space, and carry how
    // much that mapping can be trusted. The old code assumed a monitor share
    // unconditionally and asserted the result as fact — which is how a box 585px
    // from the real tile reached the report without anything noticing.
    const mapped = mapTileToSurface(rect, metrics, (_a = surface === null || surface === void 0 ? void 0 : surface.surface) !== null && _a !== void 0 ? _a : 'unknown');
    const camBox = (mapped === null || mapped === void 0 ? void 0 : mapped.camBox)
        || viewportToScreenFraction(rect, { innerW, innerH, outerW, outerH });
    if (!camBox)
        return null;
    // Native webcam frame size — only meaningful for a <video> tile. Lets the
    // report overlay reproduce the tile's object-fit crop so landmarks aren't
    // stretched across the whole tile when camera aspect ≠ tile aspect.
    const vid = el;
    const srcW = typeof vid.videoWidth === 'number' && vid.videoWidth > 0 ? vid.videoWidth : undefined;
    const srcH = typeof vid.videoHeight === 'number' && vid.videoHeight > 0 ? vid.videoHeight : undefined;
    // Explicit argument wins; otherwise read the tag the tiles already carry.
    const datasetWhich = (_b = el.dataset) === null || _b === void 0 ? void 0 : _b.proctorCam;
    const resolvedWhich = which !== null && which !== void 0 ? which : (datasetWhich === 'main' || datasetWhich === 'pip' ? datasetWhich : undefined);
    const objectPos = elementObjectPosition(el);
    // The raw transform string, not just the boolean derived from it. The mirror
    // evidence is contradictory — CSS says scaleX(-1), landmark fitting against
    // the recording says unmirrored on every span of three interviews — and a
    // boolean cannot be re-examined. The matrix can.
    const rawTransform = typeof window !== 'undefined' && window.getComputedStyle
        ? String(window.getComputedStyle(el).transform || 'none')
        : 'none';
    return Object.assign(Object.assign(Object.assign({ ts: Math.max(0, ts), camBox, 
        // VA-28223/28255: keep the working, not just the answer.
        //
        // A `camBox` is a fraction, and a fraction cannot be inverted back into the
        // rect and denominator it came from — so a wrong box is indistinguishable
        // from a right one after the fact. That is exactly how an ~11% scale error
        // survived to production: the numbers looked plausible and nothing could
        // check them. These are the terms of the division.
        metrics: {
            innerW, innerH, outerW, outerH,
            screenW: metrics.screenW,
            screenH: metrics.screenH,
            dpr: metrics.dpr,
            rectL: Math.round(rect.left),
            rectT: Math.round(rect.top),
            rectW: Math.round(rect.width),
            rectH: Math.round(rect.height),
            captureW: metrics.captureW,
            captureH: metrics.captureH,
            captureFps: Math.round((surface === null || surface === void 0 ? void 0 : surface.trackFps) || 0),
            captureSurface: (_c = surface === null || surface === void 0 ? void 0 : surface.surface) !== null && _c !== void 0 ? _c : 'unknown',
            objectPosX: objectPos[0],
            objectPosY: objectPos[1],
            transform: rawTransform,
        }, mirrored: elementIsMirrored(el), camVisible: true, fit: elementFit(el) }, (srcW && srcH ? { srcW, srcH } : {})), (resolvedWhich ? { which: resolvedWhich } : {})), (mapped
        ? Object.assign(Object.assign({ originSource: mapped.originSource, originTrusted: mapped.originTrusted, mapVersion: PROCTOR_MAP_VERSION }, (mapped.originAssumed ? { originAssumed: true } : {})), (mapped.originConvention
            ? { originConvention: mapped.originConvention }
            : {})) : {}));
}
// ── VA-28145: map a tile onto the SHARED SURFACE, and say how much to trust it ──
// 3: floating windows on a monitor share are placed rather than refused, using
// the observed `screenX/Y` convention (or disclosed as inferred). Rows at
// version 2 keep the old refusal, because their origin really was unknown —
// nothing observed it at the time.
export const PROCTOR_MAP_VERSION = 3;
/**
 * Where the camera tile sits inside the recorded frame, and whether we actually
 * know that or are assuming it.
 *
 * THE BUG THIS EXISTS FOR. `screenRectFraction` treats `window.screenX/screenY`
 * as the viewport's on-screen origin and adds the tile's viewport rect to it.
 * Measured on interview-b81757ff the stored box was (24, 81) where the true
 * tile was at (609, 200) — the SIZE correct to ~2px, the ORIGIN wrong by 585px
 * horizontally. The cause is that `screenX`/`screenY` are not dependable: on
 * Wayland they are withheld and read 0, so a window anywhere but the top-left
 * produces a box that is confidently, silently wrong. On the coding PiP the
 * error reached 1.7x the tile's own width — zero overlap with the real tile.
 *
 * The fix is not a better guess. It is to make the assumption VISIBLE, so a
 * consumer can decline to use it: the report can decline to draw, and
 * VA-28131's offline pass can decline to crop. A confident integrity score
 * computed from the wrong pixels is worse than no score.
 *
 * The branches, and why each is or is not trustworthy:
 *
 *   browser (tab share)  the captured frame IS the viewport, so the tile's
 *                        viewport rect is already surface-relative. Exact.
 *   window share         the captured frame is the browser window. The viewport
 *                        sits below the chrome, whose height is
 *                        `outerH - innerH` — a value the browser reports
 *                        directly rather than one we infer. Trustworthy.
 *   monitor share        needs the window's on-screen origin, which is the part
 *                        that is not reliably available. Trusted ONLY when the
 *                        viewport demonstrably fills the screen (fullscreen or
 *                        maximized), because then the origin is 0 by
 *                        construction and no platform can withhold it. Windowed
 *                        monitor shares are marked untrusted rather than
 *                        guessed — and that is most of the reported error.
 *   unknown surface      `displaySurface` was not reported. Fall back to
 *                        today's behaviour and mark it untrusted.
 *
 * Note this resolves the `screenY` question the overlay plan §7 flags as
 * blocked on a non-Wayland machine WITHOUT needing the measurement: where the
 * two readings of `screenY` would disagree (a windowed monitor share) the
 * result is untrusted either way, and where they agree (fullscreen, chrome
 * height 0) the answer does not depend on which is right. The measurement is
 * still worth taking — it would let the windowed case be trusted rather than
 * disclosed — but nothing is blocked on it.
 */
// ── Which origin does this browser report? ───────────────────────────────────
//
// `screenX`/`screenY` are the one unknown that stops a FLOATING window being
// placed on a monitor share. Per CSSOM View they are the VIEWPORT origin, with
// the browser chrome already excluded. Not every engine agrees: some report the
// WINDOW origin, and then the chrome has to be added back. The two differ by the
// chrome height — 80-130px — which is enough to draw landmarks on the address
// bar, so guessing is not acceptable.
//
// It does not have to be guessed. A window that is MAXIMIZED but not fullscreen
// has its outer origin at (0, 0) by definition, so reading `screenY` in that
// state answers the question outright:
//
//   screenY ~ 0                  -> it reports the WINDOW origin
//   screenY ~ outerH - innerH    -> it reports the VIEWPORT origin
//
// Those are ~100px apart, so the discrimination is unambiguous. One observation
// settles it for this browser, and exiting fullscreen usually restores to a
// maximized window — which is exactly when the answer starts to matter.
/** The rect a MAXIMIZED window fills, and where its top-left sits.
 *
 * `screen` is the whole display; `screen.avail*` is what is left once the
 * desktop's own furniture is deducted. A maximized window fills the latter, so
 * comparing `outerH` against `screenH` marks every maximized window on a
 * desktop with a top bar or dock as floating — short by exactly the bar.
 */
function availArea(m) {
    return {
        w: m.availW && m.availW > 0 ? m.availW : m.screenW,
        h: m.availH && m.availH > 0 ? m.availH : m.screenH,
        left: m.availLeft || 0,
        top: m.availTop || 0,
    };
}
const CONVENTION_KEY = 'proctor-origin-convention';
let conventionCache = null;
export function knownOriginConvention() {
    if (conventionCache)
        return conventionCache;
    try {
        const stored = localStorage.getItem(CONVENTION_KEY);
        if (stored === 'viewport' || stored === 'window') {
            conventionCache = stored;
            return stored;
        }
    }
    catch (_a) {
        // Private mode / blocked storage: the probe still works per-session.
    }
    return null;
}
/** Only ever returns a verdict from a state that can actually prove one. */
export function probeOriginConvention(m) {
    const avail = availArea(m);
    const windowFillsAvail = avail.w > 0 && avail.h > 0 &&
        m.outerW >= avail.w - 2 && m.outerH >= avail.h - 2;
    const viewportFillsScreen = m.innerH >= m.screenH - 2;
    // Fullscreen proves nothing: there the chrome is ~0 and both readings agree.
    if (!windowFillsAvail || viewportFillsScreen)
        return null;
    const chromeTop = m.outerH - m.innerH;
    // Need a chrome tall enough that the two candidates cannot be confused.
    if (chromeTop < 24)
        return null;
    // A maximized window's own top edge is `availTop`, not 0 — the desktop's bar
    // sits above it. So the two candidate readings are `availTop` (the window's
    // edge) and `availTop + chromeTop` (the viewport's, chrome deducted).
    const asWindow = Math.abs(m.screenY - avail.top);
    const asViewport = Math.abs(m.screenY - (avail.top + chromeTop));
    const verdict = asViewport < asWindow ? 'viewport' : 'window';
    conventionCache = verdict;
    try {
        localStorage.setItem(CONVENTION_KEY, verdict);
    }
    catch (_a) {
        // Session-only is still better than nothing.
    }
    return verdict;
}
export function mapTileToSurface(rect, m, surface) {
    if (rect.width <= 0 || rect.height <= 0)
        return null;
    // ── VA-28255 item 7: map in ONE unit system ────────────────────────────────
    //
    // THE BUG THIS REPLACES. `getBoundingClientRect()` returns ZOOMED css pixels —
    // page zoom is already applied. `screen.width/height` and `outerWidth/Height`
    // return UNZOOMED device-independent pixels — page zoom does not touch them.
    // The branches below divide the first by the second. A fraction is invariant
    // only when both terms share a unit, so at any zoom but 100% the result is
    // wrong by exactly the zoom factor.
    //
    // Measured on interview-a7bf7924 at ~90% zoom: the stored box was 0.890 wide
    // and clamped to 1.000 tall, where the recording says 0.798 x 0.9167. Both
    // errors, and the clamp, come out of that one factor.
    //
    // THE FIX. `devicePixelRatio` is device px per ZOOMED css px and tracks zoom,
    // so it converts the rect into the recording's own units. The capture's own
    // dimensions are the denominator — the actual surface, not an assumption
    // about which surface it is.
    const dpr = m.dpr && m.dpr > 0 ? m.dpr : 0;
    const capW = m.captureW && m.captureW > 0 ? m.captureW : 0;
    const capH = m.captureH && m.captureH > 0 ? m.captureH : 0;
    if (capW && capH && dpr && (surface === 'monitor' || surface === 'window')) {
        // Device px, so directly comparable with the capture.
        const tileW = rect.width * dpr;
        const tileH = rect.height * dpr;
        // Does the viewport itself cover the whole capture? That is the real test
        // for "fullscreen", and unlike `innerW >= screenW` it is unit-correct — the
        // old test silently flips to false at zoom > 100% on a genuinely fullscreen
        // page, and to true on a merely tall one.
        const viewportIsSurface = Math.abs(m.innerW * dpr - capW) <= 4 && Math.abs(m.innerH * dpr - capH) <= 4;
        if (viewportIsSurface) {
            const box = viewportFraction(rect, m.innerW, m.innerH);
            return box
                ? { camBox: box, originSource: 'monitor', originTrusted: true }
                : null;
        }
        // Not fullscreen: the viewport sits somewhere inside the captured surface,
        // and the offset to it is only available in unzoomed px. Convert with the
        // capture's own scale rather than assuming it equals `dpr` — for a monitor
        // share the capture IS the screen, so this ratio is exactly the conversion.
        if (surface === 'monitor' && m.screenW > 0 && m.screenH > 0) {
            const sx = capW / m.screenW;
            const sy = capH / m.screenH;
            const convention = knownOriginConvention();
            const chromeTop = convention === 'window' ? Math.max(0, m.outerH - m.innerH) : 0;
            const chromeLeft = convention === 'window' ? Math.max(0, (m.outerW - m.innerW) / 2) : 0;
            return {
                camBox: {
                    x: clamp01(((m.screenX + chromeLeft) * sx + rect.left * dpr) / capW),
                    y: clamp01(((m.screenY + chromeTop) * sy + rect.top * dpr) / capH),
                    w: clamp01(tileW / capW),
                    h: clamp01(tileH / capH),
                },
                originSource: 'monitor',
                originTrusted: convention !== null,
                originAssumed: convention === null,
                originConvention: convention !== null && convention !== void 0 ? convention : undefined,
            };
        }
        if (surface === 'window' && m.outerW > 0 && m.outerH > 0) {
            // The window frame is reported unzoomed; the capture is the window.
            const sx = capW / m.outerW;
            const sy = capH / m.outerH;
            const chromeTop = Math.max(0, m.outerH - m.innerH);
            const chromeLeft = Math.max(0, (m.outerW - m.innerW) / 2);
            return {
                camBox: {
                    x: clamp01((chromeLeft * sx + rect.left * dpr) / capW),
                    y: clamp01((chromeTop * sy + rect.top * dpr) / capH),
                    w: clamp01(tileW / capW),
                    h: clamp01(tileH / capH),
                },
                originSource: 'window-share',
                originTrusted: true,
            };
        }
    }
    // ── end item 7; below is the pre-VA-28255 path, kept for rows with no
    //    capture dimensions (older clients) so their behaviour is unchanged ─────
    if (surface === 'browser') {
        const box = viewportFraction(rect, m.innerW, m.innerH);
        return box ? { camBox: box, originSource: 'screen-api', originTrusted: true } : null;
    }
    if (surface === 'window') {
        // The window frame includes the chrome; the viewport starts below it.
        const chromeTop = Math.max(0, m.outerH - m.innerH);
        const chromeLeft = Math.max(0, Math.round((m.outerW - m.innerW) / 2));
        if (!m.outerW || !m.outerH)
            return null;
        return {
            camBox: {
                x: clamp01((chromeLeft + rect.left) / m.outerW),
                y: clamp01((chromeTop + rect.top) / m.outerH),
                w: clamp01(rect.width / m.outerW),
                h: clamp01(rect.height / m.outerH),
            },
            originSource: 'window-share',
            originTrusted: true,
        };
    }
    // Two situations give a KNOWN origin on a monitor share, and the first
    // version of this only recognised one of them.
    //
    // FULLSCREEN: the viewport fills the screen, so the origin is (0,0) and no
    // platform can withhold it.
    //
    // MAXIMIZED: the WINDOW fills the screen — so the window is at (0,0) and the
    // viewport is inset from it by the browser chrome, which the browser reports
    // directly as `outerH - innerH`. The origin is just as knowable; it is simply
    // not zero.
    //
    // Requiring the VIEWPORT to fill the screen treated every non-fullscreen
    // interview as unplaceable — because outside fullscreen the chrome makes that
    // impossible by construction. Live-verified on interview-2fc99686: the
    // candidate left fullscreen at t=126 and every layout row after it was marked
    // untrusted, so the report drew nothing over 304 capture rows that had a face
    // in every single one. Refusing to draw on an unknown origin is right;
    // calling a maximized window unknown was not.
    const fillsScreen = m.screenW > 0 &&
        m.screenH > 0 &&
        m.innerW >= m.screenW - 2 &&
        m.innerH >= m.screenH - 2;
    const avail = availArea(m);
    const windowFillsScreen = avail.w > 0 &&
        avail.h > 0 &&
        m.outerW >= avail.w - 2 &&
        m.outerH >= avail.h - 2;
    if (surface === 'monitor' && !fillsScreen && windowFillsScreen) {
        // Maximized: measure from the window's own origin at (0,0), offset by the
        // chrome. No dependence on screenX/screenY, which is what was unreliable.
        const chromeTop = Math.max(0, m.outerH - m.innerH);
        const chromeLeft = Math.max(0, Math.round((m.outerW - m.innerW) / 2));
        // The window's own origin is the available area's corner, not (0,0): the
        // desktop's top bar sits above it and its dock may sit to the left. Adding
        // only the chrome placed the tile that bar's height too HIGH on the
        // recording, which is the upward shift reported after leaving fullscreen.
        return {
            camBox: {
                x: clamp01((avail.left + chromeLeft + rect.left) / m.screenW),
                y: clamp01((avail.top + chromeTop + rect.top) / m.screenH),
                w: clamp01(rect.width / m.screenW),
                h: clamp01(rect.height / m.screenH),
            },
            originSource: 'monitor',
            originTrusted: true,
        };
    }
    // FLOATING window on a monitor share — the case that used to be refused.
    //
    // The arithmetic was never the problem; the meaning of `screenX/Y` was. With
    // the convention observed we can place the viewport exactly, so this becomes
    // as trustworthy as the maximized branch. Without it we still place the tile,
    // but say the position was inferred rather than measured — a reviewer can then
    // discount a ~100px discrepancy instead of being shown nothing at all.
    if (surface === 'monitor' && !fillsScreen) {
        probeOriginConvention(m);
        const convention = knownOriginConvention();
        const chromeTop = convention === 'window' ? Math.max(0, m.outerH - m.innerH) : 0;
        const chromeLeft = convention === 'window' ? Math.max(0, Math.round((m.outerW - m.innerW) / 2)) : 0;
        if (m.screenW > 0 && m.screenH > 0) {
            return {
                camBox: {
                    x: clamp01((m.screenX + chromeLeft + rect.left) / m.screenW),
                    y: clamp01((m.screenY + chromeTop + rect.top) / m.screenH),
                    w: clamp01(rect.width / m.screenW),
                    h: clamp01(rect.height / m.screenH),
                },
                originSource: 'monitor',
                // Trusted only once the convention has actually been observed.
                originTrusted: convention !== null,
                // Otherwise: drawn, but declared as inferred rather than measured.
                originAssumed: convention === null,
                originConvention: convention !== null && convention !== void 0 ? convention : undefined,
            };
        }
    }
    const box = screenRectFraction(rect, m);
    if (!box)
        return null;
    return {
        camBox: box,
        originSource: surface === 'monitor' ? 'monitor' : 'assumed',
        originTrusted: surface === 'monitor' && fillsScreen,
    };
}
